import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { Mail, CheckCircle2 } from "lucide-react";

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/auth/forgot-password", { email });
      setSent(true);
    } catch (err) {
      // Defensive: show a friendly toast if the request fails outright
      // (in normal operation the endpoint always returns 200 to prevent enumeration).
      toast.error(formatApiError(err.response?.data?.detail) || "Não foi possível processar o pedido. Tenta novamente dentro de instantes.");
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen flex flex-col bg-[#0d0d0d] text-white">
      <div className="auk-ribbon h-2" />
      <div className="flex-1 flex flex-col justify-center px-6 py-8 max-w-md mx-auto w-full">
        <Link to="/login" className="text-xs text-white/60 mb-8 hover:text-white">← Voltar ao login</Link>

        {!sent ? (
          <>
            <div className="w-14 h-14 rounded-2xl bg-[#c8102e] flex items-center justify-center mb-4">
              <Mail size={26} />
            </div>
            <h1 className="font-display text-3xl font-extrabold mb-2">Recuperar acesso</h1>
            <p className="text-sm text-white/60 mb-8 leading-relaxed">
              Introduz o email da tua conta. Vamos enviar-te um link seguro para criares uma nova palavra-passe.
            </p>

            <form onSubmit={submit} className="space-y-3">
              <input
                data-testid="forgot-email"
                type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                placeholder="O teu email"
                className="w-full px-4 py-3 rounded-xl bg-white/10 border border-white/20 text-white placeholder-white/40 outline-none focus:border-[#c8102e] transition-colors"
              />
              <button
                data-testid="forgot-submit"
                type="submit" disabled={busy}
                className="w-full py-3 rounded-xl bg-[#c8102e] hover:bg-[#a50c24] disabled:opacity-50 font-semibold transition-colors"
              >
                {busy ? "A enviar..." : "Enviar link de recuperação"}
              </button>
            </form>
          </>
        ) : (
          <div data-testid="forgot-sent" className="text-center">
            <div className="w-14 h-14 rounded-2xl bg-green-600 flex items-center justify-center mb-4 mx-auto">
              <CheckCircle2 size={28} />
            </div>
            <h1 className="font-display text-2xl font-extrabold mb-2">Verifica o teu email</h1>
            <p className="text-sm text-white/70 leading-relaxed mb-6">
              Se existir uma conta associada a <strong className="text-white">{email}</strong>, receberás um email com um link para redefinir a palavra-passe.
              O link expira em <strong className="text-white">1 hora</strong> e só pode ser usado uma vez.
            </p>
            <p className="text-xs text-white/50 leading-relaxed">
              Não recebeste? Verifica a pasta de spam ou tenta novamente dentro de alguns minutos.
            </p>
            <Link to="/login" className="inline-block mt-8 text-[#ffd100] font-semibold text-sm hover:underline">
              Voltar ao login
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}
